/**
 * CRE Agent Mode — the CRE patient page running inside the Windows dictation
 * agent (docs/cre-agent-mode/PLAN.md).
 *
 * Opt-in only: the agent opens CRE with `?agent=1`. The flag is kept in
 * sessionStorage for this window (the app's auth redirects rebuild the URL
 * without it), and `?agent=0` turns it off again. A normal CRE tab never has
 * the flag, so nothing changes for it.
 *
 * Compact / expanded is the agent's own window control (its own button), so
 * CRE gets the whole window; the editor simply fits the window size.
 */

export const AGENT_MODE_QUERY_PARAM = "agent";
export const AGENT_MODE_STORAGE_KEY = "cre.agentMode";

function safeSession() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

/**
 * Read `?agent=` from a search string and update sessionStorage.
 * @returns {boolean} agent mode after applying the URL
 */
export function syncAgentModeFromSearch(search, storage = safeSession()) {
  const value = new URLSearchParams(search || "").get(AGENT_MODE_QUERY_PARAM);
  if (!storage) return value === "1" || value === "true";
  try {
    if (value === "1" || value === "true") storage.setItem(AGENT_MODE_STORAGE_KEY, "1");
    else if (value === "0" || value === "false") {
      storage.removeItem(AGENT_MODE_STORAGE_KEY);
    }
    return storage.getItem(AGENT_MODE_STORAGE_KEY) === "1";
  } catch (_) {
    return value === "1" || value === "true";
  }
}

let agentModeFlag = false;

/** Evaluate the flag once at startup, before any auth redirect rewrites the URL. */
export function initCreAgentMode(search = typeof window !== "undefined" ? window.location.search : "") {
  agentModeFlag = syncAgentModeFromSearch(search);
  return agentModeFlag;
}

/** True when this window was opened by the Windows agent (`?agent=1`). Not auth-aware. */
export function isAgentModeFlagSet() {
  return agentModeFlag;
}

/** Test helper. */
export function resetCreAgentModeForTests() {
  agentModeFlag = false;
}
